import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, BedDouble, Maximize, MountainSnow, Check } from "lucide-react";
import { siteConfig } from "../config/siteConfig";

export default function RoomDetailModal({ room, open, onOpenChange, onBookNow }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    setIdx(0);
  }, [room]);

  if (!room) return null;

  const images = room.images || [];
  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);
  const next = () => setIdx((i) => (i + 1) % images.length);

  const waLink = `https://wa.me/${siteConfig.contact.whatsapp}?text=${encodeURIComponent(
    `Hello THURULIYA HERITAGE, I'd like to inquire about the ${room.name}.`
  )}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={`room-detail-${room.id}`}
        className="bg-[#FAF9F6] border-0 sm:max-w-5xl rounded-3xl p-0 max-h-[92vh] overflow-y-auto overflow-x-hidden"
      >
        {/* Image carousel */}
        <div className="relative aspect-[16/9] bg-[#1A362D] overflow-hidden rounded-t-3xl">
          {images.map((src, i) => (
            <img
              key={src}
              src={src}
              alt={`${room.name} ${i + 1}`}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                i === idx ? "opacity-100" : "opacity-0"
              }`}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />

          {images.length > 1 && (
            <>
              <button
                onClick={prev}
                aria-label="Previous image"
                data-testid="room-image-prev"
                className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#FAF9F6]/85 hover:bg-[#C5A059] text-[#1A362D] flex items-center justify-center backdrop-blur transition-colors"
              >
                <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
              </button>
              <button
                onClick={next}
                aria-label="Next image"
                data-testid="room-image-next"
                className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#FAF9F6]/85 hover:bg-[#C5A059] text-[#1A362D] flex items-center justify-center backdrop-blur transition-colors"
              >
                <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </>
          )}

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`Image ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-8 bg-[#C5A059]" : "w-1.5 bg-[#FAF9F6]/70"
                }`}
              />
            ))}
          </div>

          <div className="absolute top-4 left-4 bg-[#1A362D]/95 backdrop-blur text-[#C5A059] px-4 py-1.5 rounded-full text-[10px] tracking-[0.22em] uppercase">
            {room.priceNote}
          </div>
        </div>

        {/* Thumbnails */}
        {images.length > 1 && (
          <div className="px-6 sm:px-10 pt-4 flex gap-2 overflow-x-auto">
            {images.map((src, i) => (
              <button
                key={src}
                onClick={() => setIdx(i)}
                className={`shrink-0 w-20 h-14 sm:w-24 sm:h-16 overflow-hidden rounded-xl border-2 transition-all ${
                  i === idx ? "border-[#C5A059]" : "border-transparent opacity-60 hover:opacity-100"
                }`}
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        {/* Details */}
        <div className="px-6 sm:px-10 py-8">
          <p className="font-overline mb-3">Heritage Stay</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#1A362D] mb-4 leading-tight">
            {room.name}
          </h2>
          <div className="gold-divider mb-6"></div>

          <p className="text-[#2C2C2C] leading-relaxed mb-6 text-base sm:text-lg">
            {room.longDescription || room.shortDescription}
          </p>

          <div className="grid grid-cols-3 gap-4 mb-8 border-y border-[#EBE5D9] py-5">
            <div className="flex items-center gap-3">
              <BedDouble className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} />
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#5C4033]">Bed</p>
                <p className="text-sm text-[#1A362D]">{room.bed}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Maximize className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} />
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#5C4033]">Size</p>
                <p className="text-sm text-[#1A362D]">{room.size}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <MountainSnow className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} />
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#5C4033]">View</p>
                <p className="text-sm text-[#1A362D]">{room.view}</p>
              </div>
            </div>
          </div>

          <h3 className="font-overline mb-4">Room Features</h3>
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-y-3 gap-x-6 mb-10">
            {room.facilities.map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm text-[#2C2C2C]">
                <Check className="w-4 h-4 text-[#C5A059]" strokeWidth={2} />
                {f}
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => onBookNow?.()}
              className="btn-primary"
              data-testid="room-detail-book-now"
            >
              Book Now
            </button>
            <a
              href={waLink}
              target="_blank"
              rel="noreferrer"
              className="btn-outline-dark"
              data-testid="room-detail-whatsapp"
            >
              Inquire on WhatsApp
            </a>
          </div>
          <p className="text-xs text-[#5C4033]/80 mt-4">
            Availability is confirmed directly via WhatsApp, Booking.com or Agoda.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
